import React from 'react';
import { levelerStateLabel } from './levelerIcons';
import MobileGateCardOpenButton from './MobileGateCardOpenButton';

/**
 * Phase 5.G PR-2/PR-5 — gate card rendered on phones (in place of the
 * data-table-as-cards CSS hack).
 *
 * Layout (top → bottom):
 *   (1) status accent stripe on the RTL "start" edge
 *   (2) name + status pill + optional select checkbox
 *   (3) tag row: model / group / leveler chip (when relevant)
 *   (4) footer: cycles · compact open/close button (PR-5)
 *
 * The footer's open/close button is a compact mirror of GateControlPanel
 * — same confirm dialog, same backend route — so customers can fire a
 * command without first opening the slide panel. Tap anywhere else on
 * the card still opens the full panel.
 */

function statusToneFor(g) {
  if (g.installation_status === 'not_started') return 'unset';
  if (g.is_open) return 'open';
  if (g.cycle_limit && g.cycle_count / g.cycle_limit >= 0.9) return 'warning';
  return 'closed';
}

function statusLabel(tone, isOpen) {
  if (tone === 'unset') return 'לא מוגדר';
  return isOpen ? 'פתוח' : 'סגור';
}

function formatLevelerElapsed(changedAt) {
  if (!changedAt) return null;
  const ms = Date.now() - new Date(changedAt).getTime();
  if (ms < 0) return null;
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) return `${minutes} דק'`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  return rem ? `${hours} ש' ${rem} דק'` : `${hours} ש'`;
}

function MobileGateCard({
  gate: g,
  selected,
  onToggleSelect,
  onClick,
  selectable,
  canIssueCommands = false,
  onCommandComplete,
  pinned = false,
  onTogglePin = null,
}) {
  const tone = statusToneFor(g);
  const levelerLabel = g.is_continuous_with_leveler ? levelerStateLabel(g) : null;
  const levelerElapsed = g.is_continuous_with_leveler
    ? formatLevelerElapsed(g.leveler_state_changed_at)
    : null;
  const levelerWorking = g.leveler_state === 'working';

  const handleCardClick = (e) => {
    // Don't open the slide panel when the user is just tapping the
    // checkbox or the command button — both stop propagation themselves
    // but a tap on the surrounding label / padding shouldn't navigate.
    if (e.target.closest('.biz-gate-card-checkbox')) return;
    if (e.target.closest('.biz-gate-card-cmd-btn')) return;
    if (e.target.closest('.biz-gate-card-pin-btn')) return;
    if (onClick) onClick(g);
  };

  return (
    <div
      className={`biz-gate-card biz-gate-card--${tone}${selected ? ' is-selected' : ''}${pinned ? ' is-pinned' : ''}`}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(e); } }}
    >
      <div className="biz-gate-card-top">
        <div className="biz-gate-card-name-block">
          <div className="biz-gate-card-name">{g.name}</div>
          <div className={`biz-gate-card-status biz-gate-card-status--${tone}`}>
            <span className="dot" aria-hidden="true" />
            {statusLabel(tone, g.is_open)}
          </div>
        </div>
        <div className="biz-gate-card-top-actions">
          {onTogglePin && (
            <button
              type="button"
              className={`biz-gate-card-pin-btn${pinned ? ' is-active' : ''}`}
              onClick={(e) => { e.stopPropagation(); onTogglePin(g.id); }}
              aria-label={pinned ? `הסר הצמדה: ${g.name}` : `הצמד למעלה: ${g.name}`}
              title={pinned ? 'הסר מהמוצמדים' : 'הצמד למעלה'}
            >
              {pinned ? '★' : '☆'}
            </button>
          )}
          {selectable && (
            <label
              className="biz-gate-card-checkbox"
              onClick={(e) => e.stopPropagation()}
              aria-label={`בחר את שער ${g.name}`}
            >
              <input
                type="checkbox"
                checked={!!selected}
                onChange={() => onToggleSelect && onToggleSelect(g.id)}
              />
            </label>
          )}
        </div>
      </div>

      {(g.model || g.group_name || levelerLabel) && (
        <div className="biz-gate-card-meta">
          {g.model && <span className="biz-gate-card-meta-item">{g.model}</span>}
          {g.group_name && <span className="biz-gate-card-meta-item is-tag">{g.group_name}</span>}
          {levelerLabel && (
            <span className={`biz-gate-card-meta-item is-leveler ${levelerWorking ? 'is-working' : ''}`}>
              {levelerLabel}{levelerElapsed && ` · ${levelerElapsed}`}
            </span>
          )}
        </div>
      )}

      <div className="biz-gate-card-footer">
        <span className="biz-gate-card-footer-item">
          <span className="biz-gate-card-footer-label">סייקלים</span>
          {(g.cycle_count || 0).toLocaleString()}
        </span>
        <MobileGateCardOpenButton
          gate={g}
          canIssueCommands={canIssueCommands}
          onCommandComplete={onCommandComplete}
        />
      </div>
    </div>
  );
}

export default MobileGateCard;
